import { LOGGER } from './Logger'

export { merge, get } from 'lodash'

export const flatten = (...values: any[]) => {
  return values.reduce((acc, val) => {
    if (Array.isArray(val)) { acc.push(...val) }
    return acc
  }, [])
}

export const combine = (...objects: any[]) => {
  return objects.reduce((acc, obj) => {
    if (!obj) { return acc }
    return { ...acc, ...obj }
  }, {})
}

export const set = (object: any, path: string, value: any, splitKey = '.') => {
  let current = object
  const pathArr = path.split(splitKey)
  for (let i = 0; i < pathArr.length - 1; i++) {
    const elem = pathArr[i]
    if (!current[elem]) { current[elem] = {} }
    current = current[elem]
  }
  current[pathArr[pathArr.length - 1]] = value
  return object
}

export const pluck = (items: any[], key: string) => items.map(item => item[key])

type IndexByKey<T> = keyof T | ((v: T) => string)

export function indexBy<T extends Object>(values: T[], key: IndexByKey<T>): { [key: string]: T[] }
export function indexBy<T extends Object>(values: T[], key: IndexByKey<T>, unique: true): { [key: string]: T }

export function indexBy<T extends Object>(values: T[], key: IndexByKey<T>, unique?: boolean) {
  const blanks: T[] = []
  const indexed = values.reduce((acc: any, val) => {
    const i = typeof key === 'function' ? key(val) : val[key]
    if (unique) {
      if (acc[i] !== undefined) { LOGGER.warn('Already a matching value, they should be unique', acc[i]) }
      acc[i] = val
    } else {
      if (i === undefined) {
        blanks.push(val)
      } else {
        if (acc[i] === undefined) { acc[i] = [] }
        acc[i].push(val)
      }
    }
    return acc
  }, {})

  if (blanks.length) { LOGGER.warn(`Indexing by "${key}" but found ${blanks.length} undefined values`, blanks) }
  return indexed
}

export const indexGridCoordinates = (grid: any[][], idKey: string) => {
  const warnings: any[] = []
  const result = grid.reduce((index, row, i) => {
    row.forEach((cell, j) => {
      const id = cell[idKey]
      if (id) {
        if (index[id] !== undefined) {
          warnings.push({ message: 'duplicate id', id })
          return
        }
        index[id] = [i, j]
      } else {
        warnings.push({ message: 'cell has no id', cell })
      }
    })
    return index
  }, {} as { [id: string]: [number, number] })
  if (warnings.length) { LOGGER.warn('An grid index had some warnings', warnings) }
  return result
}

// String helpers
export const toSnakeCase = (string: string) => {
  string = string.replace(/\W+/g, ' ').split(' ').join('_')
  if (string.charAt(string.length - 1) === '_') { return string.substring(0, string.length - 1) }
  return string
}
